// Backtest card — mirrors legacy .bt-card layout: header (symbol + status badge),
// param meta rows, and conditionally the 5-stat results + Deploy Grid button.
import { useState } from "react";
import { fmt } from "@/lib/format";
import BacktestStats from "./backtest-stats";
import CreateGridModal from "@/components/grid/create-grid-modal";
import type { BacktestRecord } from "@/types/api";

const statusCls: Record<BacktestRecord["status"], string> = {
  pending: "bg-warn/10 text-warn",
  running: "bg-info/10 text-info",
  done: "bg-success/10 text-success",
  error: "bg-danger/10 text-danger",
};

export default function BacktestCard({ bt }: { bt: BacktestRecord }) {
  const [showDeploy, setShowDeploy] = useState(false);

  const isDone = bt.status === "done" && bt.total_trades != null;

  return (
    <>
      <div className="bg-surface border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-base text-text">{bt.symbol}</span>
          <span
            className={`text-[0.65rem] uppercase px-2 py-0.5 rounded ${statusCls[bt.status]}`}
          >
            {bt.status}
          </span>
        </div>

        <div className="text-xs text-muted space-y-0.5">
          <div>
            Range: {fmt(bt.lower_price)} – {fmt(bt.upper_price)} · {bt.grid_count} levels ·{" "}
            {bt.quantity_per_grid} qty
          </div>
          <div>
            Period: {bt.start_date} to {bt.end_date}
          </div>
        </div>

        {isDone && (
          <>
            <BacktestStats
              totalTrades={bt.total_trades!}
              realizedPnl={bt.realized_pnl!}
              totalFees={bt.total_fees!}
              maxDrawdown={bt.max_drawdown!}
              winRate={bt.win_rate!}
            />
            <div className="flex justify-end mt-3">
              <button
                className="text-xs px-3 py-1.5 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors"
                onClick={() => setShowDeploy(true)}
              >
                Deploy Grid
              </button>
            </div>
          </>
        )}
      </div>

      {showDeploy && (
        <CreateGridModal
          onClose={() => setShowDeploy(false)}
          initial={{
            symbol: bt.symbol,
            lower_price: bt.lower_price,
            upper_price: bt.upper_price,
            grid_count: bt.grid_count,
            quantity_per_grid: bt.quantity_per_grid,
            leverage: 1,
            side: "neutral",
          }}
        />
      )}
    </>
  );
}
